import { useEffect, useState } from 'react'

import { Formik } from 'formik'
import * as Yup from 'yup'

import {
  AddDefaultExpenseTypeRequest,
  DefaultExpenseCategory,
  DefaultExpenseTypeForEdit,
  GetExpenseTypeForEditRequest,
  ProductIds,
  addDefaultExpenseType,
  editDefaultExpenseType,
  getDefaultExpenseCategories,
  getDefaultExpenseTypeForEdit,
} from 'core'
import { ExpenseTypeEligibility } from 'core/domain/expenses/expenseTypeEligibility'

import { Button, DefaultCheckboxV2, DefaultInputV2, DefaultSelectV2, Modal } from 'common/components'
import { useCommandRequestV2, useQueryRequestV2 } from 'common/hooks'

interface Props {
  visible: boolean
  forEditRequest: GetExpenseTypeForEditRequest | null
  isSuperAdmin: boolean
  onSaved?: (productId: number, isEdit: boolean) => void
  onCancel?: () => void
}

const formValidation = () =>
  Yup.object<AddDefaultExpenseTypeRequest>({
    name: Yup.string().required('Required'),
    productId: Yup.number().typeError('Please select an option').required('Required'),
    expenseCategoryId: Yup.number().typeError('Please select an option').required('Required'),
    active: Yup.bool().required('Required'),
    hraEligible: Yup.bool().required('Required'),
    eligibility: Yup.number().typeError('Please select an option').required('Required'),
    aiAutoVerifyEnabled: Yup.bool().notRequired(),
  })

const defaultInitialValues = {
  name: '',
  productId: null,
  expenseCategoryId: null,
  active: false,
  hraEligible: false,
  eligibility: null,
  aiAutoVerifyEnabled: false,
}

const UpsertExpenseTypeModal = ({ visible, forEditRequest, isSuperAdmin, onSaved, onCancel }: Props) => {
  const { sendQuery: sendForEditQuery } = useQueryRequestV2<DefaultExpenseTypeForEdit>()
  const { isLoading: categoriesQueryIsLoading, sendQuery: sendCategoriesQuery } =
    useQueryRequestV2<DefaultExpenseCategory[]>()

  const { sendCommand: sendAddCommand, isLoading: addCommandIsLoading } = useCommandRequestV2(addDefaultExpenseType)
  const { sendCommand: sendEditCommand, isLoading: editCommandIsLoading } = useCommandRequestV2(editDefaultExpenseType)

  const [initialValues, setInitialValues] = useState<AddDefaultExpenseTypeRequest>(defaultInitialValues)
  const [categories, setCategories] = useState<DefaultExpenseCategory[]>([])

  const isEditModal = visible && forEditRequest !== null
  const commandIsLoading = addCommandIsLoading || editCommandIsLoading

  const updateInitialValues = async () =>
    setInitialValues(await sendForEditQuery(() => getDefaultExpenseTypeForEdit(forEditRequest)))

  const updateCategories = async (productId: number = forEditRequest?.productId) =>
    setCategories(await sendCategoriesQuery(() => getDefaultExpenseCategories(productId)))

  useEffect(() => {
    if (!visible) return
    if (forEditRequest === null) {
      !Object.is(initialValues, defaultInitialValues) && setInitialValues(defaultInitialValues)
      categories.length !== 0 && setCategories([])
      return
    }
    ;(async () => {
      await updateInitialValues()
      await updateCategories()
    })()
  }, [visible, forEditRequest])

  const sendCommandHandler = async (values: AddDefaultExpenseTypeRequest) =>
    isEditModal
      ? await sendEditCommand({
          ...values,
          expenseTypeId: forEditRequest.expenseTypeId,
          previousProductId: forEditRequest.productId,
          previousExpenseCategoryId: initialValues.expenseCategoryId,
        })
      : await sendAddCommand(values)

  return (
    <Modal
      visible={visible}
      title={isEditModal ? 'Edit expense type' : 'Add new expense type'}
      footer={null}
      onCancel={onCancel}
    >
      {visible && (
        <Formik
          enableReinitialize={true}
          validationSchema={formValidation()}
          initialValues={initialValues}
          onSubmit={async (values, { resetForm }) => {
            const res = await sendCommandHandler(values)
            res.completed && resetForm()
            res.completed && onSaved(values.productId, isEditModal)
          }}
        >
          {(formik) => (
            <form onSubmit={formik.handleSubmit}>
              <DefaultSelectV2
                name="productId"
                placeholder="Select product"
                label="Product"
                items={[
                  { id: ProductIds.IchraId, name: 'ICHRA' },
                  { id: ProductIds.QsehraId, name: 'QSEHRA' },
                  { id: ProductIds.GroupHraId, name: 'GCHRA' },
                ]}
                onChange={async (productId) => {
                  await updateCategories(productId)
                  formik.setFieldValue('expenseCategoryId', null)
                }}
              />

              <DefaultSelectV2
                name="expenseCategoryId"
                loading={categoriesQueryIsLoading}
                disabled={categories.length === 0 && !categoriesQueryIsLoading}
                placeholder={`${categories.length === 0 ? 'Select a poduct first' : 'Select category'}`}
                label="Category"
                items={categories}
              />

              <DefaultInputV2 name="name" label="Name" />

              <DefaultSelectV2
                name="eligibility"
                placeholder="Select eligibility status"
                label={'Eligibility Status'}
                items={[
                  { id: ExpenseTypeEligibility.Reimbursable, name: 'Reimbursable' },
                  {
                    id: ExpenseTypeEligibility.ReimbursableWithDoctorsNote,
                    name: 'Reimbursable With DoctorsNote',
                  },
                  {
                    id: ExpenseTypeEligibility.ReimbursableWithPrescription,
                    name: 'Reimbursable With Prescription',
                  },
                  { id: ExpenseTypeEligibility.NotReimbursable, name: 'Not Reimbursable' },
                ]}
              />

              <DefaultCheckboxV2 name="hraEligible" label="Limited HRA eligible" />

              <DefaultCheckboxV2 name="active" label="Active" />

              {isSuperAdmin && <DefaultCheckboxV2 name="aiAutoVerifyEnabled" label="AI Auto-verify" />}

              <div className="text-center">
                <Button
                  type="primary"
                  htmlType="submit"
                  disabled={
                    !formValidation().isValidSync(formik.values) ||
                    (isEditModal && JSON.stringify(formik.values) === JSON.stringify(initialValues)) ||
                    commandIsLoading
                  }
                  loading={commandIsLoading}
                >
                  {isEditModal ? 'Edit' : 'Add'}
                </Button>
                <Button className="ml-16" disabled={commandIsLoading} onClick={onCancel}>
                  Cancel
                </Button>
              </div>
            </form>
          )}
        </Formik>
      )}
    </Modal>
  )
}

export default UpsertExpenseTypeModal
